import { PrimaryBtn, GhostBtn } from "./Buttons";

interface LocateSheetProps {
  open: boolean;
  locating: boolean;
  /** 重試後仍拿不到權限（系統已記住拒絕，不會再跳窗）→ 改顯示「怎麼救回來」而不是再給一顆沒用的重試鍵 */
  blocked?: boolean;
  onAllow: () => void;
  onManual: () => void;
}

/**
 * 定位「拿不到」時的說明卡 —— 注意它不是 pre-permission 卡片。
 *
 * 正常情況下使用者根本不會看到這張卡：第一次進流程直接呼叫 getCurrentPosition，由系統自己的
 * 權限窗出面，只需要點一次。只有在 resolveLocateGate() 判定「已知定位拿不到」（先前被拒或
 * Permissions API 回報 denied）時才輪到這張卡，而那種狀態下系統窗本來就不會再跳 ——
 * 所以兩個對話框連著跳的情況不會發生。
 *
 * 兩段式：先問要不要再試（權限若還能問，按下去就會跳系統窗）；若重試仍被擋（blocked），
 * 換成告訴他怎麼救，不留一顆按了沒反應的死按鈕。
 */
const LocateSheet = ({ open, locating, blocked = false, onAllow, onManual }: LocateSheetProps) => {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-[300] flex items-center justify-center px-8">
      <div className="absolute inset-0 bg-[#0f1a35]/45 onb-overlay-fade" onClick={onManual} />
      <div className="relative w-full max-w-[320px] bg-white rounded-[20px] p-6 onb-rise">
        {blocked ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#FEF3C7] flex items-center justify-center mx-auto mb-3">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l9 16H3L12 3z" stroke="#F59E0B" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M12 10v4M12 17h.01" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <div className="text-[17px] font-extrabold text-[#0f1a35] text-center">目前無法取得位置</div>
            <div className="text-[13px] text-[#64748B] text-center mt-2 mb-5 leading-[1.6]">
              定位權限被關閉了。<b className="text-[#B45309]">手動輸入地址一樣可以完成</b>；
              想改用自動帶入的話，關掉這個頁面重新進入，並在手機詢問時選「允許」。
            </div>
            <PrimaryBtn onClick={onManual}>好，我手動輸入</PrimaryBtn>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-[#E3F2FD] flex items-center justify-center mx-auto mb-3">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 21c4.5-5.5 7-9 7-12a7 7 0 10-14 0c0 3 2.5 6.5 7 12z" stroke="#2196f3" strokeWidth="1.8" strokeLinejoin="round" />
                <circle cx="12" cy="9" r="2.6" stroke="#2196f3" strokeWidth="1.8" />
              </svg>
            </div>
            <div className="text-[17px] font-extrabold text-[#0f1a35] text-center">要幫您自動帶入地址嗎？</div>
            <div className="text-[13px] text-[#64748B] text-center mt-2 mb-5 leading-[1.6]">
              先前沒能取得您的位置。開啟定位就能<b className="text-[#1976D2]">自動填好送達地址</b>，
              不想開也沒關係，手動輸入一樣可以。
            </div>
            <div className="flex flex-col gap-2.5">
              <PrimaryBtn onClick={onAllow} busy={locating} busyText="定位中…">開啟定位，自動帶入</PrimaryBtn>
              <GhostBtn onClick={onManual}>我要手動輸入</GhostBtn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocateSheet;
